import { DeployerGridGzDataProvider } from '../dataProvider/DeployerGridGzDataProvider';
import { Column, ColumnKeys, Dependencies, PackagesWebComponentSchema } from '../types';
import { GridViewMetadata } from '../../../../grid/gridView/types';
import { Filter } from '../../../../grid/gridFilters/types';
import { ColumnDetails, ColumnType } from '../../../../grid/types/common';
import { renderDeployerRelayIp, renderDeployerRelayLabel, renderDeployerRelayName } from './customGridRenderers';
import { GridViewWebComponent } from '../../../../grid/gridView';
import { DeployerGridRelay } from '../types/deployer-grid-relay';
import { DeployerGridRowData } from '../types/deployer-grid-row-data';
import { translateFn } from '../../../../utils/translator.helper';

export class GridConfig {
  private columnRenderers = {
    [ColumnKeys.RELAY_NAME]: renderDeployerRelayName.bind(this),
    [ColumnKeys.RELAY_IP]: renderDeployerRelayIp.bind(this),
    [ColumnKeys.LABEL]: renderDeployerRelayLabel.bind(this),
  };

  private pageSizeOptions = [
    {
      title: '20',
      value: '20',
    },
    {
      title: '50',
      value: '50',
    },
    {
      title: '100',
      value: '100',
    },
  ];

  private dependencies: Dependencies;
  private $grid: GridViewWebComponent<Column, ColumnKeys, DeployerGridRelay>;

  public getDataForView(
    dependencies: Dependencies,
    grid: GridViewWebComponent<Column, ColumnKeys, DeployerGridRelay>
  ): PackagesWebComponentSchema {
    this.dependencies = dependencies;
    this.$grid = grid;

    const viewMetaData: GridViewMetadata<Column, ColumnKeys> = {
      viewId: 'deployer-grid-default-view',
      predefinedFiltersKeys: this.getPredefinedFiltersKeys(),
      columns: this.getColumns(),
      filters: this.getDefaultFilters(),
      pageSize: 20,
      isCompactViewChecked: false,
      hasSelectableRows: false,
      enableRowClickEvent: true,
      hasPagination: true,
      pageSizeOptions: this.pageSizeOptions,
      columnRenderers: this.columnRenderers,
      disableGridSettings: true,
      hideFiltersToggle: true,
      hideGridRefresh: true,
      showGlobalActions: true,
      allColumnDetails: this.getColumnDetails(),
      pageNumber: 1,
      dataProvider: new DeployerGridGzDataProvider(dependencies, this.getColumnDetails()),
      enableCSVExport: false,
      cellClassNameGenerator: this.getCellClassNameGenerator,
      columnConfigs: [],
      filtersUiConfigs: [],
    };

    return {
      viewMetaData,
    };
  }

  private getDefaultFilters(): Array<Filter<ColumnKeys>> {
    return [
      { [ColumnKeys.RELAY_NAME]: { contains: '' } },
      { [ColumnKeys.RELAY_IP]: { contains: '' } },
      { [ColumnKeys.CUSTOM_SERVER_NAME_IP]: { contains: '' } },
      { [ColumnKeys.LABEL]: { contains: '' } },
    ];
  }

  private getPredefinedFiltersKeys(): Array<ColumnKeys> {
    return [ColumnKeys.RELAY_NAME, ColumnKeys.RELAY_IP, ColumnKeys.CUSTOM_SERVER_NAME_IP, ColumnKeys.LABEL];
  }

  private getColumns(): Column[] {
    return [Column.RELAY_NAME, Column.RELAY_IP, Column.CUSTOM_SERVER_NAME_IP, Column.LABEL];
  }

  private getColumnDetails(): Array<ColumnDetails<Column, ColumnKeys>> {
    return [
      {
        column: Column.RELAY_NAME,
        key: ColumnKeys.RELAY_NAME,
        columnName: translateFn('Name'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
        filteringDisabled: false,
        flexGrow: 2,
        editingDisabled: true,
      },
      {
        column: Column.RELAY_IP,
        key: ColumnKeys.RELAY_IP,
        columnName: this.dependencies.translateFn('IP'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
        filteringDisabled: false,
        flexGrow: 1,
        editingDisabled: true,
      },
      {
        column: Column.CUSTOM_SERVER_NAME_IP,
        key: ColumnKeys.CUSTOM_SERVER_NAME_IP,
        columnName: this.dependencies.translateFn('Custom server name/IP'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
        filteringDisabled: true,
        flexGrow: 2,
        editingDisabled: false,
      },
      {
        column: Column.LABEL,
        key: ColumnKeys.LABEL,
        columnName: this.dependencies.translateFn('Label'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
        filteringDisabled: false,
        flexGrow: 1,
      },
    ];
  }

  private getCellClassNameGenerator(_, rowData: DeployerGridRowData): string {
    const {
      item: { isGhost },
    } = rowData;

    return isGhost ? 'cell-ghost' : '';
  }

  public handleDefaultColumnValue(value: string | undefined): string {
    if (value === '' || value === undefined) {
      return this.dependencies.translateFn('N/A');
    }
    return value;
  }
}
